import type { v2 } from '@deque/billing-service-client';
import { Button, Icon, LoaderOverlay } from '@deque/cauldron-react';
import classNames from 'classnames';
import queryString from 'query-string';
import React, {
  type FormEvent,
  type ReactElement,
  useCallback,
  useMemo,
  useState
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import PageTitle from '~/common/components/PageTitle';
import { useAuthContext } from '~/common/contexts/auth';
import useAPI, { type State } from '~/common/hooks/useAPI';
import useDidUpdate from '~/common/hooks/useDidUpdate';
import { type TransInterpolation } from '~/common/types';
import billingClient, {
  type Enterprises
} from '~/common/utils/billing-client/client-v1';
import globalStyles from '../app.css';
import ErrorMessage from '../components/issue/ErrorMessage';
import styles from './AcceptInvitation.css';

interface APIData {
  invitation?: v2.Invitation;
  enterprise?: Enterprises.Enterprise;
}

type APIState = State<APIData>;

const AcceptInvitation = (): ReactElement => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const history = useHistory();
  const invitationId: string = useMemo(() => {
    const { id } = queryString.parse(window.location.search);
    return id as string;
  }, []);
  const fetchInvitation = useCallback(async (): Promise<APIData> => {
    const invitation = await billingClient.enterprises.getInvitation({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      token: user!.token,
      id: invitationId as string
    });

    const enterpriseAccount = await billingClient.enterprises.get({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      token: user!.token,
      id: invitation.enterprise_id
    });

    return {
      invitation,
      enterprise: enterpriseAccount
    };
  }, []);
  const { data, error, loading }: APIState = useAPI(fetchInvitation);
  const [invitationError, setInvitationError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const accept = async () => {
    if (!data?.invitation) {
      return;
    }

    try {
      setAccepting(true);

      await billingClient.enterprises.acceptInvitation({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        token: user!.token,
        enterpriseId: data.invitation.enterprise_id,
        id: invitationId
      });

      // route to dashboard and show confirmation toast
      history.push('/?invitation-accepted=true');
    } catch {
      setAccepting(false);
      setInvitationError(t('Accepting invitation failed. Please try again.'));
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    // clean up any previous submission errors
    setInvitationError('');
    accept();
  };

  useDidUpdate(() => {
    if (loading || !data?.invitation?.accepted_at) {
      return;
    }

    // they've already accepted this invitation - route them to dashboard
    history.push('/');
  }, [loading]);

  return (
    <form
      onSubmit={onSubmit}
      className={classNames(globalStyles.wrap, styles.acceptInvitation)}
    >
      <PageTitle title={t('Accept Invitation')} />

      {error && <ErrorMessage error={error.message} />}

      {(loading || accepting) && (
        <LoaderOverlay
          label={
            accepting
              ? t('Accepting invitation...')
              : t('Loading invitation...')
          }
          focusOnInitialRender
        />
      )}

      {data?.invitation && data?.enterprise && (
        <>
          <h1>{t('You’ve been invited to Axe DevTools Extension')}</h1>
          <hr />
          <p>
            <Trans>
              You were invited by{' '}
              <strong>
                {
                  {
                    senderEmail: data.invitation.sender_email
                  } as TransInterpolation
                }
              </strong>{' '}
              at{' '}
              <strong>
                {{ enterprise: data.enterprise.name } as TransInterpolation}
              </strong>{' '}
              to access Axe DevTools.
            </Trans>
          </p>
          <p>
            {t(
              'Selecting “Accept Invitation” will grant you immediate access to Axe DevTools and associate your Deque account:'
            )}
          </p>
          <div className={classNames(globalStyles.emailBlock, styles.email)}>
            {user?.email}
          </div>
          <p>
            <Trans>
              with the{' '}
              <strong>
                {{ enterprise: data.enterprise.name } as TransInterpolation}
              </strong>
              ’s joint account.
            </Trans>
          </p>
          {invitationError && (
            <div role="alert">
              <div className="Error">{invitationError}</div>
            </div>
          )}
          <Button type="submit">
            <Icon type="check-circle" />
            {t('Accept Invitation')}
          </Button>
        </>
      )}
    </form>
  );
};

export default AcceptInvitation;
